import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ConfirmDialog from "./ConfirmDialog";

const ConfirmDialogContext = createContext(null);

let warnedAboutMissingProvider = false;
function warnMissingProvider(hookName) {
  if (warnedAboutMissingProvider || process.env.NODE_ENV === "production") return;
  warnedAboutMissingProvider = true;
  // eslint-disable-next-line no-console
  console.warn(
    `${hookName}() was called outside <ConfirmDialogProvider>; falling back to the native browser dialog. Mount the provider (App.js does) so automation can drive the in-app dialog.`,
  );
}

/**
 * Mount once near the app root. Renders a single <ConfirmDialog> and hands
 * out promise-based `confirm()` / `prompt()` functions through context.
 * Requests made while a dialog is already open are queued and shown one
 * after another.
 */
export function ConfirmDialogProvider({ children }) {
  const [active, setActive] = useState(null);
  const queueRef = useRef([]);
  const activeRef = useRef(null);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const showNext = useCallback(() => {
    const next = queueRef.current.shift() || null;
    activeRef.current = next;
    setActive(next);
  }, []);

  const enqueue = useCallback(
    (request) =>
      new Promise((resolve) => {
        const entry = { ...request, resolve };
        if (activeRef.current) {
          queueRef.current.push(entry);
        } else {
          activeRef.current = entry;
          setActive(entry);
        }
      }),
    [],
  );

  const settle = useCallback(
    (result) => {
      const current = activeRef.current;
      if (current) current.resolve(result);
      showNext();
    },
    [showNext],
  );

  const confirm = useCallback(
    (options = {}) => {
      const opts = typeof options === "string" ? { message: options } : options;
      return enqueue({ mode: "confirm", options: opts });
    },
    [enqueue],
  );

  const prompt = useCallback(
    (options = {}) => {
      const opts = typeof options === "string" ? { message: options } : options;
      return enqueue({ mode: "prompt", options: opts });
    },
    [enqueue],
  );

  const contextValue = useMemo(() => ({ confirm, prompt }), [confirm, prompt]);

  const options = active?.options || {};
  const isPrompt = active?.mode === "prompt";

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}
      <ConfirmDialog
        open={Boolean(active)}
        mode={active?.mode || "confirm"}
        title={options.title}
        message={options.message}
        confirmLabel={options.confirmLabel || (isPrompt ? "OK" : "Confirm")}
        cancelLabel={options.cancelLabel}
        danger={Boolean(options.danger)}
        defaultValue={options.defaultValue}
        placeholder={options.placeholder}
        inputLabel={options.inputLabel}
        inputType={options.inputType}
        required={Boolean(options.required)}
        testId={options.testId}
        onConfirm={(value) => settle(isPrompt ? `${value ?? ""}` : true)}
        onCancel={() => settle(isPrompt ? null : false)}
      />
    </ConfirmDialogContext.Provider>
  );
}

/**
 * const confirm = useConfirm();
 * const ok = await confirm({ title, message, confirmLabel, cancelLabel, danger });
 * Resolves `true` when confirmed, `false` when cancelled / Escape / backdrop.
 */
export function useConfirm() {
  const context = useContext(ConfirmDialogContext);
  return useCallback(
    (options = {}) => {
      if (context) return context.confirm(options);
      warnMissingProvider("useConfirm");
      const opts = typeof options === "string" ? { message: options } : options;
      const text = [opts.title, opts.message].filter(Boolean).join("\n\n");
      return Promise.resolve(
        typeof window !== "undefined" && typeof window.confirm === "function"
          ? window.confirm(text)
          : false,
      );
    },
    [context],
  );
}

/**
 * const prompt = usePrompt();
 * const value = await prompt({ title, message, inputLabel, defaultValue, placeholder, confirmLabel, required });
 * Resolves the entered string, or `null` when cancelled.
 */
export function usePrompt() {
  const context = useContext(ConfirmDialogContext);
  return useCallback(
    (options = {}) => {
      if (context) return context.prompt(options);
      warnMissingProvider("usePrompt");
      const opts = typeof options === "string" ? { message: options } : options;
      const text = [opts.title, opts.message].filter(Boolean).join("\n\n");
      const result =
        typeof window !== "undefined" && typeof window.prompt === "function"
          ? window.prompt(text, opts.defaultValue || "")
          : null;
      return Promise.resolve(result === null || result === undefined ? null : `${result}`);
    },
    [context],
  );
}

export default ConfirmDialogProvider;
